import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { useSession } from '@client/lib/useSession';
import { formatCents, parseCents } from '@shared/money';
import { todayIn } from '@shared/time';
import { createInvoiceSchema } from '@shared/schemas/invoice';
import styles from './NewInvoicePage.module.css';

/** A customer row from GET /api/v1/customers. */
interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  phone: string | null;
  email: string | null;
  isActive: boolean;
}

/** A property row from GET /api/v1/properties. */
interface Property {
  id: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
  gateCode: string | null;
  accessNotes: string | null;
  isActive: boolean;
}

/** A job row from GET /api/v1/jobs. Only COMPLETED + uninvoiced jobs are
 *  offered as billable lines. */
interface Job {
  id: string;
  status: string;
  invoicedAt: string | null;
  priceCents: number;
  title: string;
  customerId: string;
}

/** A manual line item the owner is building. `priceInput` holds the raw
 *  string being typed (e.g. "45.00"); `unitPriceCents` is the parsed
 *  integer-cents value used for the total and the POST body. */
interface ManualLine {
  description: string;
  quantity: number;
  unitPriceCents: number;
  priceInput: string;
}

/** The invoice returned by POST /api/v1/invoices. */
interface CreatedInvoice {
  id: string;
}

/**
 * The org timezone is read from the session. The session module is mocked in
 * tests, so this reads whatever the mock provides. Mirrors NewJobPage.
 */
function useOrgTimezone(): string {
  const session = useSession();
  const tz =
    (session.user as { organizationTimezone?: string } | null)?.organizationTimezone ?? 'UTC';
  return tz;
}

function customerLabel(c: Customer): string {
  const parts = [c.firstName, c.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : (c.companyName ?? c.email ?? c.id);
}

function propertyLabel(p: Property): string {
  const line2 = [p.city, p.state, p.postalCode].filter(Boolean).join(', ');
  return [p.addressLine1, line2].filter(Boolean).join(', ');
}

export function NewInvoicePage() {
  const navigate = useNavigate();
  const orgTimezone = useOrgTimezone();

  // --- Data loading ---
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // --- Form state ---
  const [customerId, setCustomerId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [lines, setLines] = useState<ManualLine[]>([]);
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Default the issue date to the ORG's today, never the browser's date.
  useEffect(() => {
    setIssueDate(todayIn(orgTimezone));
  }, [orgTimezone]);

  // Load customers on mount.
  useEffect(() => {
    let cancelled = false;
    setLoadingCustomers(true);
    apiGet<Customer[]>('/api/v1/customers')
      .then((data) => {
        if (!cancelled) setCustomers(data);
      })
      .catch(() => {
        if (!cancelled) setCustomers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCustomers(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When the customer changes, refetch THAT customer's properties and jobs,
  // and clear the chosen property and selections.
  useEffect(() => {
    if (!customerId) {
      setProperties([]);
      setPropertyId('');
      setJobs([]);
      setSelectedJobIds([]);
      return;
    }
    let cancelled = false;
    setLoadingProperties(true);
    setLoadingJobs(true);
    apiGet<Property[]>(`/api/v1/properties?customerId=${encodeURIComponent(customerId)}`)
      .then((data) => {
        if (!cancelled) setProperties(data);
      })
      .catch(() => {
        if (!cancelled) setProperties([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProperties(false);
      });

    apiGet<Job[]>('/api/v1/jobs')
      .then((data) => {
        if (!cancelled) {
          // Offer only COMPLETED, uninvoiced jobs for this customer.
          setJobs(
            data.filter(
              (j) =>
                j.customerId === customerId && j.status === 'COMPLETED' && j.invoicedAt === null
            )
          );
        }
      })
      .catch(() => {
        if (!cancelled) setJobs([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingJobs(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  // --- Manual line item helpers ---
  const addLine = () => {
    setLines((prev) => [
      ...prev,
      { description: '', quantity: 1, unitPriceCents: 0, priceInput: '' },
    ]);
  };

  const removeLine = (index: number) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLine = (
    index: number,
    field: 'description' | 'quantity' | 'priceInput',
    value: string | number
  ) => {
    setLines((prev) => {
      const copy = [...prev];
      const original = copy[index];
      if (!original) return prev;
      const line: ManualLine = {
        description: original.description,
        quantity: original.quantity,
        unitPriceCents: original.unitPriceCents,
        priceInput: original.priceInput,
      };
      if (field === 'quantity') {
        const n = Number(value);
        line.quantity = Number.isNaN(n) || n < 1 ? 1 : Math.floor(n);
      } else if (field === 'priceInput') {
        // unitPriceCents is INTEGER CENTS. Take dollars from the owner and
        // convert with parseCents. Never a float, never multiply by 100
        // yourself, never toFixed.
        line.priceInput = value as string;
        const parsed = parseCents(value as string);
        line.unitPriceCents = parsed === null ? 0 : parsed;
      } else {
        line.description = value as string;
      }
      copy[index] = line;
      return copy;
    });
  };

  // --- Job selection helpers ---
  const toggleJob = (jobId: string) => {
    setSelectedJobIds((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const selectedJob = (jobId: string): boolean => selectedJobIds.includes(jobId);

  // Running total across BOTH selected jobs and manual lines.
  const totalCents = useMemo(() => {
    const jobTotal = jobs
      .filter((j) => selectedJobIds.includes(j.id))
      .reduce((sum, j) => sum + j.priceCents, 0);
    const lineTotal = lines.reduce((sum, line) => sum + line.quantity * line.unitPriceCents, 0);
    return jobTotal + lineTotal;
  }, [jobs, selectedJobIds, lines]);

  const hasBillable = selectedJobIds.length > 0 || lines.length > 0;

  // A ref guards against double submit: two taps in one tick both run before
  // React re-renders, so a boolean state flag would not catch the second.
  const submittingRef = useRef(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasBillable || submitting) return;
    // Double-submit guard via ref, not pending state.
    if (submittingRef.current) return;
    submittingRef.current = true;

    setSubmitting(true);
    setServerError(null);

    const body = {
      customerId,
      ...(propertyId ? { propertyId } : {}),
      issueDate,
      ...(dueDate ? { dueDate } : {}),
      ...(customerMessage ? { customerMessage } : {}),
      ...(internalNotes ? { internalNotes } : {}),
      ...(selectedJobIds.length > 0 ? { jobIds: selectedJobIds } : {}),
      ...(lines.length > 0
        ? {
            items: lines.map((line) => ({
              description: line.description,
              quantity: line.quantity,
              unitPriceCents: line.unitPriceCents,
            })),
          }
        : {}),
    };

    // Validate with the shared schema before posting. The server uses the same
    // schema, so client and server agree on what is valid.
    const result = createInvoiceSchema.safeParse(body);
    if (!result.success) {
      setServerError(result.error.issues[0]?.message ?? 'Invalid invoice');
      setSubmitting(false);
      submittingRef.current = false;
      return;
    }

    try {
      const created = await apiPost<CreatedInvoice>('/api/v1/invoices', body);
      navigate(`/invoices/${created.id}`);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const handleBillAll = async () => {
    if (!customerId || submitting) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    setSubmitting(true);
    setServerError(null);

    try {
      const created = await apiPost<CreatedInvoice>('/api/v1/invoices/from-jobs', {
        customerId,
        ...(propertyId ? { propertyId } : {}),
      });
      navigate(`/invoices/${created.id}`);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setServerError(err.message);
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  return (
    <main className={styles.main}>
      <h1 className={styles.title}>Create an invoice</h1>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {/* --- Customer --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Customer</legend>
          <select
            className={styles.select}
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setPropertyId('');
            }}
            aria-label="Customer"
            disabled={loadingCustomers}
          >
            <option value="" disabled>
              {loadingCustomers ? 'Loading customers…' : 'Choose a customer'}
            </option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {customerLabel(c)}
              </option>
            ))}
          </select>
        </fieldset>

        {/* --- Property --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Property</legend>
          <select
            className={styles.select}
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            aria-label="Property"
            disabled={loadingProperties || !customerId}
          >
            <option value="" disabled>
              {loadingProperties
                ? 'Loading properties…'
                : !customerId
                  ? 'Choose a customer first'
                  : 'Choose a property'}
            </option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {propertyLabel(p)}
              </option>
            ))}
          </select>
        </fieldset>

        {/* --- Dates --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Dates</legend>
          <input
            className={styles.input}
            type="date"
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            aria-label="Issue date"
          />
          <input
            className={styles.input}
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            placeholder="Optional due date"
            aria-label="Due date"
          />
        </fieldset>

        {/* --- Billable jobs --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Billable jobs for this customer</legend>
          {loadingJobs ? (
            <p className={styles.hint}>Loading jobs…</p>
          ) : jobs.length === 0 ? (
            <p className={styles.hint}>No completed, uninvoiced jobs for this customer.</p>
          ) : (
            jobs.map((j) => (
              <label key={j.id} className={styles.jobRow}>
                <input
                  type="checkbox"
                  aria-label={`Bill job ${j.title}`}
                  checked={selectedJob(j.id)}
                  onChange={() => toggleJob(j.id)}
                />
                <span className={styles.jobTitle}>{j.title}</span>
                <span className={styles.jobPrice}>{formatCents(j.priceCents)}</span>
              </label>
            ))
          )}
        </fieldset>

        {/* --- Manual line items --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Line items</legend>
          {lines.map((line, index) => (
            <div key={index} className={styles.line}>
              <input
                className={styles.input}
                type="text"
                value={line.description}
                onChange={(e) => updateLine(index, 'description', e.target.value)}
                placeholder="Description"
                aria-label={`Description for line ${index + 1}`}
              />
              <input
                className={styles.input}
                type="number"
                min={1}
                value={line.quantity}
                onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                aria-label={`Quantity for line ${index + 1}`}
              />
              <input
                className={styles.input}
                type="text"
                value={line.priceInput}
                onChange={(e) => updateLine(index, 'priceInput', e.target.value)}
                placeholder="Price"
                aria-label={`Unit price for line ${index + 1}`}
              />
              <button
                type="button"
                className={styles.removeBtn}
                onClick={() => removeLine(index)}
                aria-label={`Remove line ${index + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className={styles.addBtn}
            onClick={addLine}
            aria-label="Add a line item"
          >
            Add line item
          </button>
        </fieldset>

        {/* --- Notes --- */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Notes</legend>
          <input
            className={styles.input}
            type="text"
            value={customerMessage}
            onChange={(e) => setCustomerMessage(e.target.value)}
            placeholder="Message for the customer"
            aria-label="Customer message"
          />
          <input
            className={styles.input}
            type="text"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            placeholder="Internal notes"
            aria-label="Internal notes"
          />
        </fieldset>

        {/* --- Total --- */}
        <div className={styles.total}>
          <span>Total</span>
          <strong>{formatCents(totalCents)}</strong>
        </div>

        {/* --- Server error --- */}
        {serverError ? (
          <div className={styles.serverError} role="alert">
            {serverError}
          </div>
        ) : null}

        {/* --- Submit --- */}
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={!hasBillable || submitting}
          aria-busy={submitting}
        >
          {submitting ? 'Creating…' : 'Create invoice'}
        </button>
        {!hasBillable && (
          <p className={styles.hint}>Select a job or add a line item to create the invoice.</p>
        )}

        {/* --- Bill everything outstanding --- */}
        <button
          type="button"
          className={styles.billAllBtn}
          onClick={handleBillAll}
          disabled={!customerId || submitting}
          aria-busy={submitting}
        >
          Bill everything outstanding for this customer
        </button>
      </form>
    </main>
  );
}
